/**
 * EncounterManager — Step-counting wrapper around EncounterSystem.
 *
 * Adds per-step gating so encounters only roll every STEPS_PER_CHECK tiles
 * instead of every animation frame. Fires a DOM event "encounter:start" when
 * a wild Surggie is spawned so GameCanvas can react without tight coupling.
 */

import { EncounterSystem, ENCOUNTER_CHANCE } from "./encounter";
import type { WildSurggie } from "./encounter";
import type { Zone } from "@/data/zones";

export const STEPS_PER_CHECK = 3;

/**
 * Payload for the new game-store-aligned encounter event ("encounter:start:v2").
 * Shape matches the game-store `startBattle` action signature so consumers can
 * pass `detail` straight through after stripping `fromWildSpriteId`.
 *
 * The legacy "encounter:start" event (detail: { wildSurggie }) is still emitted
 * alongside this one — do NOT remove handlers that read it.
 */
export type EncounterStartEvent = {
  opponentSpeciesId: string;
  opponentLevel: number;
  opponentHpMax: number;
  isWild: boolean;
  /** Set when the encounter was triggered by walking into a visible wild sprite. */
  fromWildSpriteId?: string;
};

/**
 * Minimal shape of a visible wild sprite (matches src/engine/wild-sprites.ts
 * WildSprite, narrowed to the fields the encounter system needs).
 */
export type WildSpriteTriggerInput = {
  id: string;
  speciesId: string;
  level: number;
};

export class EncounterManager {
  private encounterSystem: EncounterSystem;
  private stepsSinceLastCheck: number = 0;
  private cooldown: boolean = false;
  private lastTileX: number = -1;
  private lastTileY: number = -1;
  private pauseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(encounterSystem: EncounterSystem) {
    this.encounterSystem = encounterSystem;
  }

  /**
   * Call once per player tile step. Every STEPS_PER_CHECK steps, rolls for
   * an encounter. Fires "encounter:start" DOM event if one triggers.
   */
  onStep(roomId: string, pixelX?: number, pixelY?: number, zone?: Zone): void {
    if (pixelX !== undefined && pixelY !== undefined) {
      const TILE_SIZE = 16; // matches constants.ts
      const tileX = Math.floor(pixelX / TILE_SIZE);
      const tileY = Math.floor(pixelY / TILE_SIZE);
      if (tileX === this.lastTileX && tileY === this.lastTileY) return;
      this.lastTileX = tileX;
      this.lastTileY = tileY;
    }

    if (this.cooldown) return;

    this.stepsSinceLastCheck += 1;
    if (this.stepsSinceLastCheck < STEPS_PER_CHECK) return;

    this.stepsSinceLastCheck = 0;

    const wild = this.triggerEncounter(roomId, zone);
    if (wild) {
      this.cooldown = true;
      this.emitEncounterStart(wild);
    }
  }

  /**
   * Attempt to spawn a wild Surggie for the given room.
   * Rolls against ENCOUNTER_CHANCE and returns the WildSurggie on success, or null.
   */
  triggerEncounter(roomId: string, zone?: Zone): WildSurggie | null {
    if (zone) {
      this.encounterSystem.setZone(zone);
    } else {
      this.encounterSystem.setRoomId(roomId);
    }

    // Roll directly — bypass checkPosition's tile-dedup logic since we gate
    // on steps here instead.
    const chance = zone?.encounterChance ?? ENCOUNTER_CHANCE;
    if (Math.random() >= chance) return null;

    this.encounterSystem.triggerEncounter();
    return this.encounterSystem.getWildSurggie();
  }

  /**
   * Trigger an encounter from a visible wild sprite the player just walked
   * into. Bypasses the per-step gating and zone roll — uses the sprite's own
   * speciesId + level directly. Returns the resulting WildSurggie, or null if
   * the species couldn't be resolved or the manager is on cooldown.
   *
   * Emits both the legacy "encounter:start" and the new "encounter:start:v2"
   * events. The v2 detail includes `fromWildSpriteId` so the wild-sprites
   * manager can despawn the consumed sprite.
   */
  triggerFromWildSprite(sprite: WildSpriteTriggerInput): WildSurggie | null {
    if (this.cooldown) return null;

    const speciesIdNum = Number.parseInt(sprite.speciesId, 10);
    if (!Number.isFinite(speciesIdNum)) return null;

    const wild = this.encounterSystem.triggerEncounterWith(
      speciesIdNum,
      sprite.level,
    );
    if (!wild) return null;

    this.cooldown = true;
    this.stepsSinceLastCheck = 0;
    this.emitEncounterStart(wild, sprite.id);
    return wild;
  }

  /**
   * Pause new encounters for `ms` milliseconds. Use after a battle ends to
   * give the player a brief grace window before they can step into another
   * encounter. Calling again replaces the prior pause.
   */
  pauseEncounters(ms: number): void {
    this.cooldown = true;
    if (this.pauseTimer !== null) {
      clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
    }
    if (ms <= 0) {
      this.cooldown = false;
      return;
    }
    this.pauseTimer = setTimeout(() => {
      this.cooldown = false;
      this.stepsSinceLastCheck = 0;
      this.pauseTimer = null;
    }, ms);
  }

  /**
   * Dispatch both the legacy and v2 encounter:start events. Kept private so
   * the two emit paths (step trigger and wild-sprite trigger) stay in sync.
   */
  private emitEncounterStart(wild: WildSurggie, fromWildSpriteId?: string): void {
    if (typeof window === "undefined") return;

    // Legacy event — existing GameCanvas handler reads this shape.
    window.dispatchEvent(
      new CustomEvent<{ wildSurggie: WildSurggie; fromWildSpriteId?: string }>(
        "encounter:start",
        {
          detail: fromWildSpriteId
            ? { wildSurggie: wild, fromWildSpriteId }
            : { wildSurggie: wild },
        },
      ),
    );

    // New event — game-store-aligned shape for the proximity encounter path.
    const v2Detail: EncounterStartEvent = {
      opponentSpeciesId: String(wild.speciesId),
      opponentLevel: wild.level,
      opponentHpMax: wild.max_hp,
      isWild: true,
      ...(fromWildSpriteId ? { fromWildSpriteId } : {}),
    };
    window.dispatchEvent(
      new CustomEvent<EncounterStartEvent>("encounter:start:v2", {
        detail: v2Detail,
      }),
    );
  }

  /**
   * Clear cooldown after a battle ends so new encounters can fire.
   */
  reset(): void {
    this.cooldown = false;
    this.stepsSinceLastCheck = 0;
    this.lastTileX = -1;
    this.lastTileY = -1;
    if (this.pauseTimer !== null) {
      clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
    }
  }
}
